/**
 * screens/settings.tsx — the single "Settings" surface (merges the old Operator
 * (Tier-2) + Config (Tier-1) screens).
 *
 * Layout, top → bottom:
 *   • Everyday toggles the venue flips anytime — self check-in, walk-ins,
 *     seating, self-removal, join channels, deposit / no-show, max party,
 *     lifecycle windows, guest QR. (These were "Operator / Tier-2".)
 *   • "Queue setup" — engine config: match type (FITTED + POOL both live),
 *     max-parallel, lifecycle windows, oversize guard + threshold. This is the
 *     old "Config / Tier-1", folded in and gated by canEditTier1.
 *
 * The "Tier 1/2/3" wording is gone from the UI — the tier concept lives on only
 * as the canEditTier1 permission gate and code comments.
 */

import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, Platform } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useQueue } from '../queue/store';
import { useSession } from '../context/sessionContext';
import { Switch } from '../components/ui';
import { Mono, Stepper } from '../queue/components/atoms';
import type { Cfg1, Cfg2 } from '../queue/types';
import { SurfacePage } from '../lib/surfaceLayout';
import { useDataSource } from '../queue/dataSource';
import { useActiveQueue } from '../queue/activeQueue';
import { useQueueConfig } from '../screens/hooks/useQueueConfig';
import CreateQueueDialog from '../queue/components/createQueueDialog';
import QrCode from '../components/qrCode';
import { showToast } from '../components/toastComponent';

/**
 * `section` scopes which half renders (rev 5 Queue Detail sub-tabs):
 *   'controls' — client-editable business toggles (behaviour, channels, deposit,
 *                max party, guest QR).
 *   'setup'    — engine config (lifecycle windows, seating type), Softknack-only.
 * Default 'all' keeps the standalone screen showing everything (legacy).
 */
export default function SettingsScreen({ section = 'all' }: { section?: 'controls' | 'setup' | 'all' }) {
  const showControls = section === 'controls' || section === 'all';
  const showSetup = section === 'setup' || section === 'all';
  const { theme } = useTheme();
  const { colors } = theme;
  const { state, toggleCfg2, setCfg2, setCfg1 } = useQueue();
  const { org, accessLevel, entitlements } = useSession();
  const { isLiveSurface } = useDataSource();
  const { activeQueueId, activeQueue, queues, loading } = useActiveQueue();

  // Live-capable = data source live + signed in. A fresh org with NO queue yet
  // gets a "create your first queue" empty state (not the in-memory demo).
  const liveMode = isLiveSurface('settings');
  const noQueueYet = liveMode && !loading && queues.length === 0;
  const [createOpen, setCreateOpen] = useState(false);

  // Live when wired + a queue is selected; else the in-memory store (unchanged).
  const useLive = liveMode && !!activeQueueId;
  const live = useQueueConfig(useLive ? activeQueueId : null);

  // Tier-2 source of truth + write handlers, from whichever path.
  const c: Cfg2 = useLive && live.cfg2 ? live.cfg2 : state.cfg2;
  const onToggle = (key: keyof Cfg2) =>
    useLive ? live.setTier2(key, !c[key]) : toggleCfg2(key);
  const onSet = <K extends keyof Cfg2>(key: K, val: Cfg2[K]) =>
    useLive ? live.setTier2(key, val) : setCfg2(key, val);

  // Tier-1 / Setup config (Softknack compose-time) + write handlers.
  const cfg1: Cfg1 = useLive && live.cfg1 ? live.cfg1 : state.cfg1;
  const canEditTier1 = accessLevel === 'softknack' || accessLevel === 'client';
  const onSet1 = <K extends keyof Cfg1>(key: K, val: Cfg1[K]) => {
    if (useLive) live.setTier1(key, val);
    else setCfg1(key, val);
  };

  // Guest self-join link the QR encodes (org + queue slug). null until both exist.
  const origin = (Platform.OS === 'web' && typeof window !== 'undefined') ? window.location.origin : '';
  const guestUrl = (useLive && org?.id && activeQueue?.slug)
    ? `${origin}/?surface=guest&org=${encodeURIComponent(org.id)}&q=${encodeURIComponent(activeQueue.slug)}`
    : null;
  const isLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)/.test(origin);

  const copyGuestUrl = () => {
    if (!guestUrl) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        navigator.clipboard.writeText(guestUrl);
        showToast('success', 'Link copied', 'Guest join link is on your clipboard.');
      }
    } catch {
      showToast('error', 'Could not copy', 'Select the link text and copy manually.');
    }
  };

  // Copy matches the design prototype's _operatorVM exactly.
  const behaviour: Array<{ key: keyof Cfg2; label: string; desc: string }> = [
    { key: 'self_join', label: 'Self check-in', desc: 'Guests join themselves (QR / SUKI / chat) — no host needed.' },
    { key: 'accepting', label: 'Accepting new entries', desc: 'Turn off to pause the line; guests see “not taking new entries”.' },
    { key: 'shared_seating', label: 'Share a unit across parties', desc: 'A resource takes more than one party until its capacity is full — e.g. a communal table, a shared ride, a class slot. Off = one party per unit (exclusive).' },
    { key: 'self_removal', label: 'Guest self-removal', desc: 'Let guests leave the line from their own phone.' },
  ];
  const channels: Array<{ key: keyof Cfg2; label: string }> = [
    { key: 'ch_QR', label: 'QR' },
    { key: 'ch_VOICE', label: 'SUKI · voice' },
    { key: 'ch_WHATSAPP', label: 'Chat' },
    { key: 'ch_STAFF', label: 'Walk-in' },
  ];

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14 } as const;

  // Fresh org → create-first-queue empty state (shared CreateQueueDialog).
  if (noQueueYet) {
    return (
      <SurfacePage>
        <View style={{ width: '100%', maxWidth: 520 }}>
          <Mono style={{ fontSize: 11, letterSpacing: 1.5, color: colors.ink3, textTransform: 'uppercase' }}>Get started</Mono>
          <Text style={{ fontSize: 26, fontWeight: '700', marginTop: 4, color: colors.ink }}>Create your first queue</Text>
          <Text style={{ fontSize: 13.5, color: colors.ink3, marginTop: 4, marginBottom: 20 }}>
            A queue holds your line. Name it — it seats guests at real tables (one party per table). You can add tables and tune settings after, and add more queues later from the queue picker up top.
          </Text>
          <TouchableOpacity onPress={() => setCreateOpen(true)} style={{
            height: 46, paddingHorizontal: 20, alignSelf: 'flex-start', borderRadius: 10, backgroundColor: colors.ink, alignItems: 'center', justifyContent: 'center',
          }}>
            <Text style={{ fontSize: 14, fontWeight: '600', color: colors.white }}>Create queue</Text>
          </TouchableOpacity>
        </View>
        <CreateQueueDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      </SurfacePage>
    );
  }

  return (
    <SurfacePage>
      <View style={{ width: '100%' }}>
        {/* header — kicker + title + subtitle (left), scope pill (right). Matches
            the prototype's Controls / Setup headers. */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
          <View>
            <Mono style={{ fontSize: 11, letterSpacing: 1.5, color: colors.ink3, textTransform: 'uppercase' }}>
              {section === 'setup' ? 'Engine setup' : 'Client controls'}
            </Mono>
            <Text style={{ fontSize: 26, fontWeight: '700', color: colors.ink, marginTop: 4 }}>
              {section === 'setup' ? 'Setup' : section === 'controls' ? 'Controls' : 'Settings'}
            </Text>
            <Text style={{ fontSize: 13.5, color: colors.ink3, marginTop: 4 }}>
              {section === 'setup'
                ? 'Engine configuration — set once when the queue is created. Softknack-only.'
                : 'Business toggles the client can flip anytime. Changes apply live to the guest and host screens.'}
            </Text>
          </View>
          {/* Scope pill: CLIENT-EDITABLE (controls) / SOFTKNACK (setup). */}
          <View style={{ height: 24, paddingHorizontal: 11, borderRadius: 999, justifyContent: 'center', backgroundColor: colors.ink }}>
            <Mono style={{ fontSize: 11, fontWeight: '600', color: colors.white }}>
              {section === 'setup' ? 'SOFTKNACK' : 'CLIENT-EDITABLE'}
            </Mono>
          </View>
        </View>

        {/* queue behaviour — Controls */}
        {showControls && <>
        <View style={[card, { paddingHorizontal: 22, paddingTop: 6, paddingBottom: 16 }]}>
          <Text style={sectionLabel(colors)}>Queue behaviour</Text>
          {behaviour.map(t => (
            <View key={t.key} style={{ flexDirection: 'row', alignItems: 'center', gap: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: colors.paper3 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: colors.ink }}>{t.label}</Text>
                <Text style={{ fontSize: 12.5, color: colors.ink4, marginTop: 2 }}>{t.desc}</Text>
              </View>
              <Switch checked={!!c[t.key]} onChange={() => onToggle(t.key)} />
            </View>
          ))}
        </View>

        {/* join channels */}
        <View style={[card, { paddingHorizontal: 22, paddingVertical: 18, marginTop: 16 }]}>
          <Text style={[sectionLabel(colors), { marginBottom: 14 }]}>Join channels</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            {channels.map(t => (
              <View key={t.key} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 9, borderWidth: 1, borderColor: colors.line, borderRadius: 10, backgroundColor: colors.paper }}>
                <Switch checked={!!c[t.key]} onChange={() => onToggle(t.key)} />
                <Text style={{ fontSize: 13, fontWeight: '500', color: colors.ink }}>{t.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* deposit / no-show */}
        <View style={{ flexDirection: 'row', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
          <AmountCard
            title="Deposit to hold" desc="Charge a refundable hold to confirm a spot."
            on={c.deposit} onToggle={() => onToggle('deposit')}
            amount={c.depositAmt} onAmount={(n) => onSet('depositAmt', n)} colors={colors}
          />
          <AmountCard
            title="No-show charge" desc="Charge automatically when a party no-shows after grace."
            on={c.no_show} onToggle={() => onToggle('no_show')}
            amount={c.noShowAmt} onAmount={(n) => onSet('noShowAmt', n)} colors={colors}
          />
        </View>

        {/* max self-join party */}
        <View style={[card, { paddingHorizontal: 22, paddingVertical: 18, marginTop: 16, flexDirection: 'row', alignItems: 'center', gap: 16 }]}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 14, fontWeight: '600', color: colors.ink }}>Max self-join party size</Text>
            <Text style={{ fontSize: 12.5, color: colors.ink4, marginTop: 2 }}>Larger groups must go through the host.</Text>
          </View>
          <Stepper value={c.maxParty} onMinus={() => onSet('maxParty', Math.max(1, c.maxParty - 1))} onPlus={() => onSet('maxParty', Math.min(20, c.maxParty + 1))} />
        </View>

        {/* guest QR — a Control (operational). */}
        <View style={[card, { paddingHorizontal: 22, paddingTop: 8, paddingBottom: 18, marginTop: 16 }]}>
          <Text style={sectionLabel(colors)}>Guest QR</Text>
          <Text style={{ fontSize: 12.5, color: colors.ink4, marginTop: -2, marginBottom: 6 }}>
            Print or display this code. Guests scan it to join the line on their phone — no app needed.
          </Text>
          {!useLive ? (
            <Text style={{ fontSize: 13, color: colors.ink4, paddingVertical: 14 }}>Connect to the live API to generate a QR.</Text>
          ) : !guestUrl ? (
            <Text style={{ fontSize: 13, color: colors.ink4, paddingVertical: 14 }}>Select a queue to generate its QR.</Text>
          ) : (
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 22, flexWrap: 'wrap', paddingTop: 6 }}>
              <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 12, padding: 12, backgroundColor: '#fff' }}>
                <QrCode value={guestUrl} size={200} />
              </View>
              <View style={{ flex: 1, minWidth: 240, gap: 10 }}>
                {!c.self_join && (
                  <Text style={{ fontSize: 12.5, color: colors.ink3, lineHeight: 18 }}>
                    ⚠ Self check-in is OFF — guests who scan will see “not taking new entries”. Turn on “Self check-in queue” above to accept QR joins.
                  </Text>
                )}
                <Text style={{ fontSize: 11, color: colors.ink4, letterSpacing: 0.4, textTransform: 'uppercase' }}>Join link</Text>
                <Text selectable style={{ fontSize: 12.5, color: colors.ink, fontFamily: 'IBM Plex Mono' } as any}>{guestUrl}</Text>
                {isLocalhost && (
                  <Text style={{ fontSize: 11.5, color: colors.ink4 }}>
                    Dev URL (localhost) — won’t scan from another device. The deployed app shows the public address.
                  </Text>
                )}
                <View style={{ flexDirection: 'row', gap: 10, marginTop: 4 }}>
                  <TouchableOpacity onPress={copyGuestUrl} style={{ height: 38, paddingHorizontal: 16, borderRadius: 9, borderWidth: 1.5, borderColor: colors.ink, justifyContent: 'center' }}>
                    <Text style={{ fontSize: 13, fontWeight: '600', color: colors.ink }}>Copy link</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
        </View>

        {/* Locked footer note — what lives in Setup, not here (prototype). */}
        <View style={{ marginTop: 16, borderWidth: 1, borderStyle: 'dashed', borderColor: colors.lineStrong, borderRadius: 12, paddingVertical: 14, paddingHorizontal: 18, flexDirection: 'row', gap: 12, backgroundColor: colors.paper2 }}>
          <Text style={{ fontSize: 15 }}>🔒</Text>
          <Text style={{ flex: 1, fontSize: 12.5, color: colors.ink3, lineHeight: 19 }}>
            Not shown here: <Text style={{ fontWeight: '700' }}>match type, max-parallel, hold / max-wait windows</Text> and the allocation rule. Those live in <Text style={{ fontWeight: '700' }}>Setup</Text> — configured by Softknack when the queue is created, hidden from the client.
          </Text>
        </View>
        </>}

        {/* ── SETUP (engine config, Softknack-only) — matches the prototype ── */}
        {showSetup && <>
        {/* Products & entitlements (read-only — platform owns subscriptions). */}
        <View style={[card, { paddingHorizontal: 22, paddingTop: 8, paddingBottom: 16 }]}>
          <Text style={sectionLabel(colors)}>Products &amp; entitlements</Text>
          <Text style={{ fontSize: 12.5, color: colors.ink4, marginTop: -2, marginBottom: 4 }}>
            What this client signed up for. Tabs appear only for entitled products — switch “Preview as” in the top bar to see the gated experience.
          </Text>
          {[
            { on: entitlements.waitlist, label: 'SK Queue · Waitlist', desc: 'Guest join, host console, now-serving, operator & live view.' },
            { on: entitlements.voice, label: 'SK Voice', desc: 'Concurrency call queue (POOL) with human handoff.' },
            { on: entitlements.chat, label: 'SK Chat', desc: 'Bot-first chat queue (POOL) with agent escalation.' },
          ].map((p) => (
            <View key={p.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: colors.paper3 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: colors.ink }}>{p.label}</Text>
                <Text style={{ fontSize: 12.5, color: colors.ink4, marginTop: 2 }}>{p.desc}</Text>
              </View>
              {/* Read-only: entitlements are granted by the platform, not toggled here. */}
              <Switch checked={p.on} onChange={() => {}} />
            </View>
          ))}
        </View>

        {/* Match type — the one varying rule. */}
        <View style={[card, { padding: 22, marginTop: 16 }]}>
          <Text style={{ fontSize: 13, fontWeight: '600', color: colors.ink, marginBottom: 12 }}>
            Match type <Text style={{ fontWeight: '400', color: colors.ink4 }}>— the one varying rule</Text>
          </Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <MatchSeg title="FITTED" desc="sized resource · exclusive" active={cfg1.matchType === 'FITTED'}
              onPress={() => canEditTier1 && onSet1('matchType', 'FITTED')} colors={colors} />
            <MatchSeg title="POOL" desc="shared capacity · concurrency" active={cfg1.matchType === 'POOL'}
              onPress={() => canEditTier1 && onSet1('matchType', 'POOL')} colors={colors} />
          </View>
          <Text style={{ fontSize: 12.5, color: colors.ink3, lineHeight: 19, marginTop: 14, paddingVertical: 12, paddingHorizontal: 14, backgroundColor: colors.paper, borderRadius: 10, borderWidth: 1, borderColor: colors.line }}>
            {cfg1.matchType === 'FITTED'
              ? 'Resource has capacity; the unit has a size. Admit the smallest free resource with capacity ≥ unit size (oversize-guarded, combine-ready). Exclusive lease — one unit per resource. Committed via SK Calendar atomic write.'
              : 'Interchangeable units, no sizing. Admit while occupancy < capacity — Voice: max-parallel; Chat: least-loaded ONLINE agent under per-unit capacity. Authority is the queue’s atomic lease on the resource row.'}
          </Text>
          {cfg1.matchType === 'POOL' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginTop: 18, paddingTop: 18, borderTopWidth: 1, borderTopColor: colors.line }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>Max parallel</Text>
                <Text style={{ fontSize: 12, color: colors.ink4 }}>how many units run at once (call slots / agents)</Text>
              </View>
              <NumInput value={cfg1.maxParallel} onChange={(n) => onSet1('maxParallel', Math.max(1, n))} colors={colors} width={90} />
            </View>
          )}
        </View>

        {/* Lifecycle windows — Hold / Max wait / Ack grace / Aging cap. Grace is
            retired: the engine uses Hold as the single called-table timer. */}
        <View style={[card, { paddingHorizontal: 22, paddingTop: 8, paddingBottom: 14, marginTop: 16 }]}>
          <Text style={sectionLabel(colors)}>Lifecycle windows</Text>
          <UnitRow label="Hold" desc="how long a called resource is held" suffix="min" value={c.holdMin} onChange={(n) => onSet('holdMin', n)} colors={colors} />
          <UnitRow label="Max wait" desc="auto-expire after this long" suffix="min" value={c.maxWaitMin} onChange={(n) => onSet('maxWaitMin', n)} colors={colors} />
          <UnitRow label="Ack grace" hint="· “on my way”" desc="pauses / extends Hold when a guest replies" suffix="sec" value={cfg1.ackGraceSec} onChange={(n) => onSet1('ackGraceSec', n)} colors={colors} />
          <UnitRow label="Aging cap" hint="· Phase-2 seam" desc="capped starvation boost; 0 = pure FIFO" suffix="pts" value={cfg1.agingCap} onChange={(n) => onSet1('agingCap', n)} colors={colors} last />
        </View>

        {/* Oversize guard + threshold. Guard is FITTED-only and only bites when a
            threshold is set (spare seats > threshold → hold for a fitter table). */}
        <View style={[card, { paddingHorizontal: 22, paddingVertical: 18, marginTop: 16, gap: 14 }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>Oversize guard</Text>
              <Text style={{ fontSize: 12, color: colors.ink4, marginTop: 2 }}>Block a small unit from taking the only large resource (FITTED hoarding guard).</Text>
            </View>
            <Switch checked={!cfg1.allowOversize} onChange={() => onSet1('allowOversize', !cfg1.allowOversize)} />
          </View>
          {!cfg1.allowOversize && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, borderTopWidth: 1, borderTopColor: colors.paper2, paddingTop: 14 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>Spare-seat threshold</Text>
                <Text style={{ fontSize: 12, color: colors.ink4, marginTop: 2 }}>Max spare seats a party may take (0 = exact fit only). The guard is inert until this is set.</Text>
              </View>
              <NumInput value={cfg1.oversizeThreshold} onChange={(n) => onSet1('oversizeThreshold', Math.max(0, n))} colors={colors} width={90} />
            </View>
          )}
        </View>

        {/* Determinism note. */}
        <View style={{ marginTop: 16, borderWidth: 1, borderStyle: 'dashed', borderColor: colors.lineStrong, borderRadius: 12, paddingVertical: 14, paddingHorizontal: 18, flexDirection: 'row', gap: 12, backgroundColor: colors.paper2 }}>
          <Text style={{ fontSize: 15 }}>⚙</Text>
          <Text style={{ flex: 1, fontSize: 12.5, color: colors.ink3, lineHeight: 19 }}>
            The allocation engine is <Text style={{ fontWeight: '700' }}>deterministic</Text> — identical line + free resources + config always yield the same decision. No randomness, no wall-clock branching. SUKI / the LLM only captures intent and party size; it has no path to pick a resource.
          </Text>
        </View>
        </>}
      </View>
      <CreateQueueDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </SurfacePage>
  );
}

/** Segmented match-type card (Setup). */
function MatchSeg({ title, desc, active, onPress, colors }: {
  title: string; desc: string; active: boolean; onPress: () => void; colors: any;
}) {
  return (
    <TouchableOpacity onPress={onPress}
      style={{ flex: 1, padding: 14, borderRadius: 10, borderWidth: 1.5, backgroundColor: active ? colors.ink : colors.card, borderColor: active ? colors.ink : colors.line }}>
      <Mono style={{ fontWeight: '600', fontSize: 15, color: active ? colors.white : colors.ink }}>{title}</Mono>
      <Text style={{ fontSize: 11, marginTop: 3, color: active ? colors.white : colors.ink4, opacity: active ? 0.7 : 1 }}>{desc}</Text>
    </TouchableOpacity>
  );
}

/** A bare numeric input (Max parallel etc.). */
function NumInput({ value, onChange, colors, width = 70 }: {
  value: number; onChange: (n: number) => void; colors: any; width?: number;
}) {
  return (
    <TextInput value={String(value)} onChangeText={(v) => onChange(Math.max(0, parseInt(v || '0', 10)))}
      keyboardType="number-pad"
      style={{ width, height: 38, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 9, paddingHorizontal: 12, fontFamily: 'IBM Plex Mono', fontSize: 15, textAlign: 'center', color: colors.ink } as any} />
  );
}

/** A labelled numeric input row with a unit suffix (lifecycle windows). */
function UnitRow({ label, hint, desc, suffix, value, onChange, colors, last }: {
  label: string; hint?: string; desc: string; suffix: string; value: number; onChange: (n: number) => void; colors: any; last?: boolean;
}) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 14, borderBottomWidth: last ? 0 : 1, borderBottomColor: colors.paper3 }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{label}{hint ? <Text style={{ fontWeight: '400', color: colors.ink4 }}> {hint}</Text> : null}</Text>
        <Text style={{ fontSize: 12, color: colors.ink4 }}>{desc}</Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <NumInput value={value} onChange={onChange} colors={colors} /><Text style={{ fontSize: 12, color: colors.ink4, width: 26 }}>{suffix}</Text></View>
    </View>
  );
}

function AmountCard({ title, desc, on, onToggle, amount, onAmount, colors }: {
  title: string; desc: string; on: boolean; onToggle: () => void;
  amount: number; onAmount: (n: number) => void; colors: any;
}) {
  return (
    <View style={{ flex: 1, minWidth: 240, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, paddingHorizontal: 22, paddingVertical: 18 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 14, fontWeight: '600', color: colors.ink }}>{title}</Text>
          <Text style={{ fontSize: 12.5, color: colors.ink4, marginTop: 2 }}>{desc}</Text>
        </View>
        <Switch checked={on} onChange={onToggle} />
      </View>
      {on && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 14, paddingTop: 14, borderTopWidth: 1, borderTopColor: colors.paper3 }}>
          <Text style={{ fontSize: 13, color: colors.ink3 }}>Amount</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', height: 36, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 8, overflow: 'hidden', backgroundColor: colors.paper }}>
            <View style={{ paddingHorizontal: 10, borderRightWidth: 1, borderRightColor: colors.line, height: '100%', justifyContent: 'center' }}>
              <Mono style={{ color: colors.ink3 }}>₹</Mono>
            </View>
            <TextInput value={String(amount)} onChangeText={(v) => onAmount(Math.max(0, parseInt(v || '0', 10)))}
              keyboardType="number-pad" style={{ width: 80, fontFamily: 'IBM Plex Mono', fontSize: 14, paddingHorizontal: 10, color: colors.ink } as any} />
          </View>
        </View>
      )}
    </View>
  );
}

function sectionLabel(colors: any) {
  return { paddingTop: 14, paddingBottom: 4, fontSize: 12, fontWeight: '600' as const, textTransform: 'uppercase' as const, letterSpacing: 0.6, color: colors.ink4 };
}
